import React from 'react';
import styled, { css } from 'styled-components';

/**
 * Lightweight filter-pill primitives for the assessment results table.
 * Re-implements athena's `elements/Filter.tsx` UX (free-text, multi-select and
 * numeric range pills) in enki's styled-components stack. Each pill renders an
 * inactive (dashed) or active (solid) state and opens an inline dropdown.
 */

const PillButton = styled.button<{ isActive: boolean }>`
  display: inline-flex;
  align-items: center;
  gap: 8px;
  padding: 6px 12px;
  border-radius: 16px;
  font-size: 13px;
  font-weight: 500;
  cursor: pointer;
  white-space: nowrap;
  background-color: ${({ theme }) => theme.colors.bgDefaultSecondary};
  color: ${({ theme }) => theme.colors.textRegular};
  transition: background-color 0.2s ease, border-color 0.2s ease;

  ${({ isActive, theme }) =>
    isActive
      ? css`
          border: 1px solid ${theme.colors.buttonPrimary}66;
          color: ${theme.colors.textActive};
        `
      : css`
          border: 1px dashed ${theme.colors.divider};
        `}

  &:hover {
    background-color: ${({ theme }) => theme.colors.bgDefault};
  }
`;

const PillIcon = styled.i`
  font-size: 11px;
`;

const PillValue = styled.span`
  color: ${({ theme }) => theme.colors.textPrimary};
  max-width: 180px;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
`;

const Dropdown = styled.div`
  position: absolute;
  top: calc(100% + 6px);
  left: 0;
  z-index: 10;
  min-width: 220px;
  padding: 12px;
  border-radius: 8px;
  border: 1px solid ${({ theme }) => theme.colors.divider};
  background-color: ${({ theme }) => theme.colors.bgPage2};
  box-shadow: 0 8px 24px rgba(0, 0, 0, 0.3);
  display: flex;
  flex-direction: column;
  gap: 8px;
`;

const PillContainer = styled.div`
  position: relative;
  display: inline-block;
`;

const TextInput = styled.input`
  width: 100%;
  box-sizing: border-box;
  padding: 8px 10px;
  border-radius: 6px;
  border: 1px solid ${({ theme }) => theme.colors.divider};
  background-color: ${({ theme }) => theme.colors.inputBackground};
  color: ${({ theme }) => theme.colors.textPrimary};
  font-size: 13px;

  &::placeholder {
    color: ${({ theme }) => theme.colors.textPlaceholder};
  }
`;

const CheckboxRow = styled.label`
  display: flex;
  align-items: center;
  gap: 8px;
  font-size: 13px;
  color: ${({ theme }) => theme.colors.textRegular};
  cursor: pointer;
  user-select: none;
`;

const RangeRow = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
`;

const RangeInput = styled(TextInput)`
  width: 72px;
`;

const RangeSeparator = styled.span`
  font-size: 13px;
  color: ${({ theme }) => theme.colors.textRegular};
`;

const RangeUnit = styled.span`
  font-size: 12px;
  color: ${({ theme }) => theme.colors.textPlaceholder};
`;

function useClickOutside(
  ref: React.RefObject<HTMLElement>,
  onOutside: () => void
) {
  React.useEffect(() => {
    const handler = (event: MouseEvent) => {
      if (ref.current && !ref.current.contains(event.target as Node)) {
        onOutside();
      }
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, [ref, onOutside]);
}

function PillShell({
  label,
  isActive,
  valueLabel,
  onClear,
  children,
}: {
  label: string;
  isActive: boolean;
  valueLabel?: string;
  onClear: () => void;
  children: (close: () => void) => React.ReactNode;
}) {
  const [isOpen, setIsOpen] = React.useState(false);
  const containerRef = React.useRef<HTMLDivElement>(null);
  useClickOutside(containerRef, () => setIsOpen(false));

  return (
    <PillContainer ref={containerRef}>
      <PillButton
        type="button"
        isActive={isActive}
        onClick={() => setIsOpen((prev) => !prev)}
      >
        <PillIcon
          className={isActive ? 'fa-solid fa-xmark' : 'fa-solid fa-plus'}
          // On an active pill the leading icon clears the filter instead of
          // opening the dropdown.
          onClick={
            isActive
              ? (e) => {
                  e.stopPropagation();
                  onClear();
                }
              : undefined
          }
        />
        {label}
        {isActive && valueLabel && (
          <>
            <span style={{ opacity: 0.5 }}>|</span>
            <PillValue>{valueLabel}</PillValue>
          </>
        )}
        <PillIcon className="fa-solid fa-chevron-down" style={{ opacity: 0.6 }} />
      </PillButton>
      {isOpen && <Dropdown>{children(() => setIsOpen(false))}</Dropdown>}
    </PillContainer>
  );
}

export function TextFilterPill({
  label,
  placeholder,
  value,
  onChange,
}: {
  label: string;
  placeholder?: string;
  value: string;
  onChange: (value: string) => void;
}) {
  return (
    <PillShell
      label={label}
      isActive={value.trim().length > 0}
      valueLabel={value.trim()}
      onClear={() => onChange('')}
    >
      {() => (
        <TextInput
          autoFocus
          placeholder={placeholder}
          value={value}
          onChange={(e) => onChange(e.target.value)}
        />
      )}
    </PillShell>
  );
}

export function MultiSelectFilterPill({
  label,
  options,
  selected,
  onChange,
}: {
  label: string;
  options: Array<{ value: string; label: string }>;
  selected: string[];
  onChange: (selected: string[]) => void;
}) {
  const valueLabel = selected
    .map((value) => options.find((o) => o.value === value)?.label ?? value)
    .join(', ');

  const toggle = (value: string) => {
    if (selected.includes(value)) {
      onChange(selected.filter((v) => v !== value));
    } else {
      onChange([...selected, value]);
    }
  };

  return (
    <PillShell
      label={label}
      isActive={selected.length > 0}
      valueLabel={valueLabel}
      onClear={() => onChange([])}
    >
      {() =>
        options.map((option) => (
          <CheckboxRow key={option.value}>
            <input
              type="checkbox"
              checked={selected.includes(option.value)}
              onChange={() => toggle(option.value)}
            />
            {option.label}
          </CheckboxRow>
        ))
      }
    </PillShell>
  );
}

export function RangeFilterPill({
  label,
  unit,
  min,
  max,
  onChange,
}: {
  label: string;
  unit?: string;
  min: number | null;
  max: number | null;
  onChange: (range: { min: number | null; max: number | null }) => void;
}) {
  const isActive = min !== null || max !== null;
  const valueLabel = `${min ?? ''}${unit ?? ''} - ${max ?? ''}${unit ?? ''}`;

  const parse = (raw: string): number | null => {
    if (raw.trim() === '') return null;
    const parsed = Number(raw);
    return Number.isNaN(parsed) ? null : parsed;
  };

  return (
    <PillShell
      label={label}
      isActive={isActive}
      valueLabel={isActive ? valueLabel : undefined}
      onClear={() => onChange({ min: null, max: null })}
    >
      {() => (
        <RangeRow>
          <RangeInput
            type="number"
            placeholder="Min"
            value={min ?? ''}
            onChange={(e) => onChange({ min: parse(e.target.value), max })}
          />
          <RangeSeparator>to</RangeSeparator>
          <RangeInput
            type="number"
            placeholder="Max"
            value={max ?? ''}
            onChange={(e) => onChange({ min, max: parse(e.target.value) })}
          />
          {unit && <RangeUnit>{unit}</RangeUnit>}
        </RangeRow>
      )}
    </PillShell>
  );
}

export const FilterBar = styled.div`
  display: flex;
  flex-direction: row;
  flex-wrap: wrap;
  align-items: center;
  gap: 12px;
  padding: 16px;
  border-top: 1px solid ${({ theme }) => theme.colors.bgActive};
`;

export const ToggleWrapper = styled.label`
  display: inline-flex;
  align-items: center;
  gap: 8px;
  font-size: 13px;
  color: ${({ theme }) => theme.colors.textRegular};
  cursor: pointer;
  user-select: none;
  margin-left: auto;
`;

const ToggleTrack = styled.span<{ checked: boolean }>`
  width: 36px;
  height: 20px;
  border-radius: 999px;
  position: relative;
  transition: background-color 0.2s ease;
  background-color: ${({ checked, theme }) =>
    checked ? theme.colors.buttonPrimary : theme.colors.bgActive};
`;

const ToggleThumb = styled.span<{ checked: boolean }>`
  position: absolute;
  top: 2px;
  left: ${({ checked }) => (checked ? '18px' : '2px')};
  width: 16px;
  height: 16px;
  border-radius: 999px;
  background-color: #fff;
  transition: left 0.2s ease;
`;

export function Toggle({
  label,
  checked,
  onChange,
}: {
  label: string;
  checked: boolean;
  onChange: (checked: boolean) => void;
}) {
  return (
    <ToggleWrapper>
      {label}
      <ToggleTrack checked={checked} role="switch" aria-checked={checked}>
        <ToggleThumb checked={checked} />
      </ToggleTrack>
      <input
        type="checkbox"
        checked={checked}
        onChange={(e) => onChange(e.target.checked)}
        style={{ display: 'none' }}
      />
    </ToggleWrapper>
  );
}
